import React, { useState } from 'react';

interface Step1Props {
    onSubmit: (data: { url?: string; description?: string }) => void;
    setStep1Data: React.Dispatch<React.SetStateAction<{
        url?: string;
        description?: string;
        summary?: string;
        company?: string;
    }>>;
}

const Step1: React.FC<Step1Props> = ({ onSubmit, setStep1Data }) => {
    const [useUrl, setUseUrl] = useState(true);
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            if (!useUrl && !description) {
                throw new Error('Please enter a business description or URL');
            }

            // Make API call
            const response = await fetch('/api/getUrlForJob', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: useUrl ? url : null,
                    description: useUrl ? null : description,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.status}`);
            }

            const data = await response.json();
            console.log('Data:', data);

            // Store both input and parsed content
            setStep1Data({
                url: useUrl ? url : undefined,
                description: useUrl ? undefined : description,
                summary: data.summary,
                company: data.company_name
            });

            // Pass data to parent
            onSubmit(useUrl ? { url } : { description });

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-medium">Business Information</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Provide information about your business to help create a better job description.
                </p>
            </div>

            <div className="flex space-x-4">
                <button
                    type="button"
                    onClick={() => setUseUrl(true)}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${useUrl
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    Website URL
                </button>
                <button
                    type="button"
                    onClick={() => setUseUrl(false)}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${!useUrl
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    Description
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {useUrl ? (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Business Website URL
                        </label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="https://www.example.com"
                            required
                        />
                    </div>
                ) : (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Business Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={6}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Describe your business..."
                            required
                        />
                    </div>
                )}

                <button
                    type="submit"
                    className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${isLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                    disabled={isLoading}
                >
                    {isLoading ? 'Loading...' : 'Next'}
                </button>
                {error && <p className="text-red-500">{error}</p>}
            </form>
        </div>
    );
};

export default Step1;